import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { X, Star, Home, ChevronRight } from "lucide-react";
import { api, getChildId } from "../../lib/api";
import { loadRecordings, play, setAudioPrefs, sfx, stopAudio } from "../../lib/audio";
import { SceneIntro, ListenCard, ChooseCard, Dialogue } from "../../components/speak/Steps";
import { SpeakPractice } from "../../components/speak/SpeakPractice";

const buildSteps = (sents) => [{ type: "scene" }, ...sents.flatMap((s) => ["listen", "choose", "say"].map((type) => ({ type, s }))), { type: "dialogue" }, { type: "reward" }];

const Reward = ({ result, topic, onHall, onNext, onRetry }) => result?.error ? (
  <div className="flex flex-col items-center gap-6 text-center" data-testid="sp-reward-error">
    <h2 className="text-3xl text-[var(--sp-deep)]">星星還沒送到，再試一次吧！</h2>
    <button data-testid="sp-reward-retry-btn" onClick={onRetry} className="kid-btn px-10 bg-[var(--sp)] text-white text-2xl">再試一次</button>
  </div>
) : (
  <div className="flex flex-col items-center gap-6 text-center" data-testid="sp-reward">
    <div className="text-7xl">{topic.emoji}</div>
    <h2 className="text-4xl sm:text-5xl text-[var(--sp-deep)]">你說得真好！</h2>
    <div className="flex gap-2" data-testid="sp-reward-stars">
      {[1, 2, 3].map((i) => (
        <motion.span key={i} initial={{ scale: 0, rotate: -40 }} animate={{ scale: 1, rotate: 0 }} transition={{ delay: 0.2 + i * 0.25, type: "spring" }}>
          <Star size={64} className={i <= (result?.stars || 0) ? "text-amber-400" : "text-stone-200"} fill="currentColor" />
        </motion.span>
      ))}
    </div>
    {result?.new_badges?.map((b) => (
      <div key={b.id} className="px-6 py-3 rounded-full bg-white border-4 border-amber-300 text-2xl" data-testid={`sp-reward-badge-${b.id}`}>{b.icon} 得到徽章：{b.name}</div>
    ))}
    <div className="flex flex-wrap justify-center gap-4 mt-2">
      <button data-testid="sp-reward-hall-btn" onClick={onHall} className="kid-btn px-8 bg-white border-2 border-[var(--sp-soft-b)] text-[var(--sp-deep)] text-2xl flex items-center gap-2"><Home /> 回到館裡</button>
      {result?.next_topic && <button data-testid="sp-reward-next-btn" onClick={onNext} className="kid-btn px-8 bg-[var(--sp)] text-white text-2xl flex items-center gap-2">下一課 <ChevronRight /></button>}
    </div>
  </div>
);

export default function SpeakLesson() {
  const { topicId } = useParams();
  const nav = useNavigate();
  const childId = getChildId();
  const [data, setData] = useState(null);
  const [child, setChild] = useState(null);
  const [i, setI] = useState(0);
  const [result, setResult] = useState(null);
  const score = useRef({ q: 0, first: 0 });
  const t0 = useRef(Date.now());
  useEffect(() => {
    if (!childId) return nav("/profiles");
    setI(0); setResult(null); score.current = { q: 0, first: 0 }; t0.current = Date.now();
    loadRecordings();
    api.get("/children").then(({ data: kids }) => { const c = kids.find((k) => k.child_id === childId); setChild(c); setAudioPrefs(c?.settings); });
    api.get(`/speak/topics/${topicId}`, { params: { child_id: childId } }).then(({ data }) => setData(data)).catch(() => nav("/speak"));
    return () => stopAudio();
  }, [topicId, childId, nav]);
  const steps = useMemo(() => (data ? buildSteps(data.sentences) : []), [data]);
  if (!data || !child) return <div className="min-h-screen speak-bg" />;
  const { topic, sentences } = data;
  const step = steps[i];
  const next = () => setI((x) => Math.min(x + 1, steps.length - 1));
  const event = (kind, sentence_id, correct) => api.post("/speak/event", { child_id: childId, topic_id: topic.topic_id, kind, sentence_id, correct }).catch(() => {});
  const scored = (first) => { score.current.q += 1; if (first) score.current.first += 1; };
  const findSentence = (text) => sentences.find((x) => x.text === text)?.sentence_id || null;
  const finish = async () => {
    setI(steps.length - 1);
    setResult(null);
    const { q, first } = score.current;
    try {
      const { data: r } = await api.post("/speak/complete", { child_id: childId, topic_id: topic.topic_id, score: q ? first / q : 1, duration_sec: Math.round((Date.now() - t0.current) / 1000) });
      setResult(r);
      sfx("good");
      play("你說得真好！");
    } catch {
      setResult({ error: true });
    }
  };
  const n = step.s ? sentences.indexOf(step.s) + 1 : null;
  return (
    <div className="min-h-screen speak-bg font-round flex flex-col" data-testid="speak-lesson-page">
      <header className="flex items-center gap-3 px-4 sm:px-8 py-3">
        <button data-testid="sp-exit-btn" onClick={() => nav("/speak")} className="w-12 h-12 rounded-full bg-white border-2 border-[var(--sp-soft-b)] flex items-center justify-center"><X /></button>
        <div className="text-2xl text-[var(--sp-deep)] truncate">{topic.emoji} {topic.title}</div>
        {n && <div className="ml-auto text-lg text-stone-500 shrink-0" data-testid="sp-sentence-counter">第 {n} / {sentences.length} 句</div>}
      </header>
      <div className="px-4 sm:px-8"><div className="h-3 rounded-full bg-white overflow-hidden border border-[var(--sp-soft-b)]">
        <div className="h-full bg-[var(--sp)] transition-[width] duration-500" style={{ width: `${(i / (steps.length - 1)) * 100}%` }} data-testid="sp-progress-bar" />
      </div></div>
      <main className="flex-1 flex items-center justify-center px-4 sm:px-8 py-8">
        <motion.div key={i} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} className="w-full">
          {step.type === "scene" && <SceneIntro topic={topic} onNext={next} />}
          {step.type === "listen" && <ListenCard s={step.s} emoji={topic.emoji} onListen={() => event("listen", step.s.sentence_id)} onNext={next} />}
          {step.type === "choose" && <ChooseCard s={step.s} all={sentences} emoji={topic.emoji} onAnswer={(c) => event("choose", step.s.sentence_id, c)} onNext={(first) => { scored(first); next(); }} />}
          {step.type === "say" && (
            <div className="flex flex-col items-center gap-4">
              <h2 className="text-3xl sm:text-4xl text-[var(--sp-deep)]">跟著說</h2>
              <SpeakPractice key={step.s.sentence_id} text={step.s.text} url={step.s.audio_url} ruby={step.s.ruby} testid="sp-say"
                onRecorded={() => event("record", step.s.sentence_id)} onDone={() => { event("speak", step.s.sentence_id); next(); }} />
            </div>
          )}
          {step.type === "dialogue" && <Dialogue topic={topic} child={child} findSentence={findSentence} onEvent={event} onScore={scored}
            onAnswer={(c, sid) => sid && event("choose", sid, c)} onNext={finish} />}
          {step.type === "reward" && <Reward result={result} topic={topic} onRetry={finish} onHall={() => nav("/speak")} onNext={() => nav(`/speak/${result.next_topic}`)} />}
        </motion.div>
      </main>
    </div>
  );
}
